/**
 * A "saia" de uma parede fina — o que se pinta POR BAIXO de meia tile de cada
 * lado de um tramo vertical de parede (e de uma abertura sem porta).
 *
 * Porque é que isto existe como módulo próprio, e não como três linhas dentro do
 * `OfficeSpace`: foi ali dentro que nasceu o bug dos «vãos pretos perto das
 * paredes» que o Kawã fotografou. A capa fina (`drawWallTopSlim`) tem as margens
 * TRANSLÚCIDAS de propósito — é isso que a faz parecer fina —, portanto o que
 * estiver por baixo aparece. O varrimento pintava o chão do vizinho de cada
 * lado, mas quando o vizinho era parede MACIÇA fazia `continue` e não pintava
 * nada: através da margem via-se o fundo da cena (`#0f151b`). O vão preto.
 *
 * A correcção é uma pergunta melhor. Não é «que CHÃO há do lado de lá» (às vezes
 * não há nenhum, e a resposta era `null`) — é «o que CONTINUA do lado de lá»,
 * que tem sempre resposta:
 *
 *   chão pisável  → o piso desse lado (madeira, alcatifa, …)
 *   fora do prédio → relva
 *   parede maciça  → PEDRA (`WALL_TOP`) — a capa de topo do vizinho
 *
 * Nunca `null`, logo nunca um buraco. E é semanticamente certo: sob aquela meia
 * tile o que existe mesmo é a pedra da parede ao lado, não o vazio.
 *
 * Aqui só se escolhe a CHAVE da textura; quem a desenha (posição, profundidade,
 * `setCrop`) continua a ser o `OfficeSpace`. Assim o mapa inteiro pode ser
 * varrido num teste sem Phaser nenhum — que é o que apanha o próximo vão antes
 * de o Kawã ter de o fotografar.
 */

import { floorAt, isOutside, TILE } from "@greenvoice/shared";

import { floorKey, GRASS, GRASS_ALT, ROOF, ROOF_ALT, WALL_TOP } from "./officeArt";
import { eTelhado } from "./officeRoof";

/**
 * Os dois lados de um tramo vertical, com o `x` do `setCrop` correspondente
 * (coordenadas da TEXTURA: a metade esquerda começa em 0, a direita a meio).
 */
export const LADOS_DA_SAIA = [
  { dc: -1, x: 0 },
  { dc: 1, x: TILE / 2 },
] as const;

/**
 * A textura que continua na tile `(c + dc, r)`, vista de cima. Nunca vazia.
 *
 * O `alt` é o xadrez do piso (tiles alternadas), calculado com as coordenadas da
 * PAREDE e não do vizinho, para a meia tile alinhar com a fiada dela e não abrir
 * um degrau de tom a meio.
 */
export function texturaDoLado(c: number, r: number, dc: number): string {
  const alt = (c + r) % 2 === 0;
  const kind = floorAt(c + dc, r);
  if (kind) return floorKey(kind, alt);
  if (isOutside(c + dc, r)) return alt ? GRASS_ALT : GRASS;
  // A parede do bloco do 1.º andar tem telhado de um dos lados.
  if (eTelhado(c + dc, r)) return alt ? ROOF_ALT : ROOF;
  return WALL_TOP;
}

/** Os dois lados já resolvidos, prontos a desenhar. */
export function saiaDaParede(c: number, r: number): readonly { key: string; x: number }[] {
  return LADOS_DA_SAIA.map(({ dc, x }) => ({ key: texturaDoLado(c, r, dc), x }));
}
